/* eslint-disable no-extra-bind */
import * as _ from 'lodash';
import Quill from 'quill';

import { whiteListStyles } from 'constants/quill';

const Format = Quill.require('format');
const dom = Quill.require('dom');
const Range = Quill.require('range');
const Delta = Quill.require('delta');

/**
 * Style format (heading1, heading2, ...)
 */
Format.FORMATS.style = {
  type: Format.types.LINE,
  class: 'style_',
  default: 'normal',
};

function intersectFormat(attrObjs) {
  const format = {};
  const attrValues = {};

  _.forEach(attrObjs, (attrObj) => {
    _.forEach(attrObj, (attrValue, attrName) => {
      if (!attrValues[attrName]) {
        attrValues[attrName] = [attrValue];
      } else {
        attrValues[attrName].push(attrValue);
      }
    });
  });

  _.forEach(attrValues, (attrValue, attrName) => {
    if (attrValue.length === attrObjs.length) {
      format[attrName] = attrValue[0];
    }
  });

  return format;
}

Quill.prototype.getFormat = function (range) {
  range = range || this.getSelection();

  if (!range) {
    return {};
  }

  return _.defaults({}, this.getLeafFormat(range), this.getLineFormat(range));
};

Quill.prototype.getLeafFormat = function (range) {
  let contents;

  if (range.isCollapsed()) {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [line, offset] = this.editor.doc.findLineAt(range.start);

    if (offset === 0) {
      contents = this.getContents(range.start, range.end + 1);
    } else {
      contents = this.getContents(range.start - 1, range.end);
    }
  } else {
    contents = this.getContents(range);
  }

  return intersectFormat(_.map(contents.ops, 'attributes'));
  /* eslint-enable no-unused-vars */
};

Quill.prototype.getLineFormat = function (range) {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  let [firstLine] = this.editor.doc.findLineAt(range.start);
  let [lastLine] = this.editor.doc.findLineAt(range.end);
  const lines = [];

  if (lastLine && lastLine === firstLine) {
    lastLine = lastLine.next;
  }

  while (firstLine !== lastLine) {
    lines.push(firstLine);
    firstLine = firstLine.next;
  }

  return intersectFormat(_.map(lines, 'formats'));
  /* eslint-enable no-unused-vars */
};

Quill.prototype.setFormat = function (name, value, isLeafExpanded) {
  this.focus();
  const range = this.getSelection();

  if (range.isCollapsed() && isLeafExpanded) {
    const [leaf, offset] = this.editor.doc.findLeafAt(range.start, true);
    range.start = range.start - offset;
    range.end = range.start + leaf.length;
  }

  if (range.isCollapsed()) {
    this.prepareFormat(name, value);
  } else {
    this.formatText(range, name, value);
  }
};

Quill.prototype.setBackwardSelection = function () {
  this.setSelection(...args);

  if (typeof window.getSelection !== 'undefined') {
    const sel = window.getSelection();
    const range = sel.getRangeAt(0);

    if (typeof sel.extend !== 'undefined') {
      const endRange = range.cloneRange();
      endRange.collapse(false);
      sel.removeAllRanges();
      sel.addRange(endRange);
      sel.extend(range.startContainer, range.startOffset);
    }
  }
};

Quill.prototype.setForwardSelection = function () {
  this.setSelection(...args);

  if (typeof window.getSelection !== 'undefined') {
    const sel = window.getSelection();
    const range = sel.getRangeAt(0);

    if (typeof sel.extend !== 'undefined') {
      const endRange = range.cloneRange();
      endRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(endRange);
      sel.extend(range.endContainer, range.endOffset);
    }
  }
};

/**
 * Token manipulation
 */
dom.EMBED_TAGS.INPUT = 'INPUT';

Format.FORMATS.token = {
  name: 'token',
  tag: 'AR-TOKEN',
  add(node, value) {
    if (_.isObject(value)) {
      _.forEach(value, (v, k) => {
        node.setAttribute(`data-${k}`, v);
      });
    }

    node.innerHTML = node.textContent;

    return node;
  },

  value(node) {
    const value = {};

    _.forEach(node.attributes, (attr) => {
      value[attr.name.replace(/^data-/, '')] = attr.value;
    });

    return value;
  },
};

_.map(whiteListStyles, (style) => {
  Format.FORMATS[style] = {
    name: style,
    style,
  };
});

Quill.registerModule('token', function (quill, options = {}) {
  const onTokenNormalize = options.onTokenNormalize || (() => true);
  const zwc = String.fromCharCode(8203); // Zero width chars

  const insertToken = function insertToken(tokenText, attrs, selection) {
    this.focus();
    const range = this.getSelection() || selection;
    this.updateContents(
      new Delta().retain(range.start).insert(zwc).insert(tokenText, { token: attrs }).insert(zwc),
      Quill.sources.USER
    );
  }.bind(quill);

  const isToken = function isToken(index) {
    const op = this.getContents(index, index + 1).ops[0];
    return (op && op.attributes && op.attributes.token) || false;
  }.bind(quill);

  const isZwc = function isZwc(index) {
    const op = this.getContents(index, index + 1).ops[0];
    return (op && op.insert === zwc) || false;
  }.bind(quill);

  const getTokenRange = function getToken(index) {
    const range = new Range(index, index);

    while (isToken(range.end)) {
      range.end++;
    }

    while (isToken(range.start - 1)) {
      range.start--;
    }

    return range;
  }.bind(quill);

  const normalizeTokens = function normalizeTokens() {
    if (!onTokenNormalize()) {
      return;
    }

    _.forEach(this.root.querySelectorAll('ar-token'), (tokenEl) => {
      const zwcEl = document.createTextNode(zwc);

      if (tokenEl.nextSibling) {
        if (tokenEl.nextSibling.textContent.substr(0, 1) !== zwc) {
          tokenEl.parentElement.insertBefore(zwcEl, tokenEl.nextSibling);
        }
      } else {
        tokenEl.parentElement.appendChild(zwcEl);
      }

      if (tokenEl.previousSibling) {
        if (tokenEl.previousSibling.textContent.slice(-1) !== zwc) {
          tokenEl.parentElement.insertBefore(zwcEl, tokenEl);
        }
      } else {
        tokenEl.parentElement.insertBefore(zwcEl, tokenEl);
      }
    });
  }.bind(quill);

  const handleTextChange = function handleTextChange() {
    _.defer(normalizeTokens);
  }.bind(quill);

  const handleSelectionChange = function handleSelectionChange() {
    const sel = this.getSelection();

    if (sel && !this._mouseSelecting) {
      if (sel.isCollapsed()) {
        const curIdx = sel.start;

        if (isZwc(curIdx) && isToken(curIdx - 1)) {
          // TODO:50 NOT WORKING??
          // The cursor now is right after token but before zwc
          const tokenRange = this.getTokenRange(curIdx - 1);

          // Set a cursor to be before zwc that is right before the token
          this.setSelection(tokenRange.start - 1, tokenRange.start - 1, Quill.sources.SILENT);
        }

        if (isZwc(curIdx - 1) && isToken(curIdx)) {
          // TODO:60 NOT WORKING??
          // The cursor now is right before token but after zwc
          const tokenRange = this.getTokenRange(curIdx);

          // Set a cursor to be before zwc that is right before the token
          this.setSelection(tokenRange.end + 1, tokenRange.end + 1, Quill.sources.SILENT);
        }
      } else {
        const { start, end } = sel;
        const nextRange = new Range(start, end);
        const lKE = this._lastKeyboardEvent;
        const isSpecialLeftKeyPressed = lKE && lKE.keyCode === 37; // Cmd + Shift + <-
        const isSpecialRightKeyPressed = lKE && lKE.keyCode === 39; // Cmd + Shift + <-
        let backwards = false;

        if (!window.getSelection().isCollapsed) {
          // find direction of selection
          const nativeSel = window.getSelection();
          const nativeRange = document.createRange();
          nativeRange.setStart(nativeSel.anchorNode, nativeSel.anchorOffset);
          nativeRange.setEnd(nativeSel.focusNode, nativeSel.focusOffset);

          backwards = nativeRange.collapsed;
        }

        if (isToken(start - 1)) {
          // The selection's start is now at the end of token but before zwc
          if (isSpecialRightKeyPressed) {
            nextRange.start = this.getTokenRange(start - 1).end + 1;
          } else {
            nextRange.start = this.getTokenRange(start - 1).start - 1;
          }
        }

        if (isToken(start)) {
          // The selection's start is now at the start of token but after zwc
          if (isSpecialLeftKeyPressed) {
            nextRange.start = this.getTokenRange(start).start - 1;
          } else {
            nextRange.start = this.getTokenRange(start).end + 1;
          }
        }

        if (isToken(end)) {
          // The selection's end is now at the start of token but after zwc
          if (isSpecialLeftKeyPressed) {
            nextRange.end = this.getTokenRange(end).start - 1;
          } else {
            nextRange.end = this.getTokenRange(end).end + 1;
          }
        }

        if (isToken(end - 1)) {
          // The selection's end is now at the end of token but before zwc
          if (isSpecialRightKeyPressed) {
            nextRange.end = this.getTokenRange(end - 1).end + 1;
          } else {
            nextRange.end = this.getTokenRange(end - 1).start - 1;
          }
        }

        if (!nextRange.equals(sel)) {
          if (backwards) {
            this.setBackwardSelection(nextRange, 'silent');
          } else {
            this.setForwardSelection(nextRange, 'silent');
          }
        }
      }
    }
  }.bind(quill);

  const handleKeyDown = function handleKeyDown(e) {
    this._lastKeyboardEvent = e;
    return true;
  }.bind(quill);

  quill.onModuleLoad('keyboard', (keyboard) => {
    const handleBackspaceKey = keyboard.hotkeys[dom.KEYS.BACKSPACE].pop().callback;
    keyboard.addHotkey(dom.KEYS.BACKSPACE, function (sel) {
      if (sel && sel.isCollapsed()) {
        const curIdx = sel.start;

        if (isToken(curIdx - 2) && isZwc(curIdx - 1)) {
          const tokenRange = getTokenRange(curIdx - 2);

          tokenRange.start--;
          tokenRange.end++;

          quill.deleteText(tokenRange.start, tokenRange.end, Quill.sources.USER);
          return false;
        }
      }

      return handleBackspaceKey(...args);
    });

    const handleDeleteKey = keyboard.hotkeys[dom.KEYS.DELETE].pop().callback;
    keyboard.addHotkey(dom.KEYS.DELETE, function (sel) {
      if (sel && sel.isCollapsed()) {
        const curIdx = sel.start;

        if (isToken(curIdx + 1)) {
          const tokenRange = getTokenRange(curIdx + 1);

          tokenRange.start--;
          tokenRange.end++;

          quill.deleteText(tokenRange.start, tokenRange.end, Quill.sources.USER);
          return false;
        }
      }

      return handleDeleteKey(...args);
    });
  });

  // TODO:190 rewrite undo-manager
  quill.onModuleLoad('undo-manager', (undoManager) => {
    undoManager._getLastChangeIndex = function (delta) {
      return delta.ops.reduce((idx, op) => {
        if (op.insert) {
          return idx + op.insert.length;
        }

        if (op.retain) {
          return idx + op.retain;
        }

        return idx;
      }, 0);
    };
  });

  const handleMouseDown = function handleMouseDown() {
    document.addEventListener('mouseup', handleMouseUp);
    this._mouseSelecting = true;
    return true;
  }.bind(quill);

  const handleMouseUp = function () {
    document.removeEventListener('mouseup', handleMouseUp);
    this._mouseSelecting = false;
    return true;
  }.bind(quill);

  quill.insertToken = insertToken;
  quill.isToken = isToken;
  quill.isZwc = isZwc;
  quill.getTokenRange = getTokenRange;

  quill.on(Quill.events.TEXT_CHANGE, handleTextChange);
  quill.on(Quill.events.SELECTION_CHANGE, handleSelectionChange);

  dom(quill.root).on('keydown', handleKeyDown);
  dom(quill.root).on('mousedown', handleMouseDown);
});
